import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import FilterToolbar from './FilterToolbar';
import { debounce } from 'lodash';

const API_URL = '/api/empresas';

const getDefaultFilters = () => ({
  q: '',
  // No default date filters for empresas
  startDate: '',
  endDate: '',
  statuses: [],
});

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

function EmpresaList() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ key: 'nombre', direction: 'asc' });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmpresas = debounce(async (currentFilters, currentSortConfig) => {
      try {
        setLoading(true);
        const params = {
          q: currentFilters.q,
          sort_by: currentSortConfig.key,
          sort_direction: currentSortConfig.direction,
        };
        const response = await axios.get(API_URL, { params });
        setEmpresas(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching empresas:', error);
        setError('No se pudieron cargar las empresas.');
      } finally {
        setLoading(false);
      }
    }, 500);

    fetchEmpresas(filters, sortConfig);

    return () => {
      fetchEmpresas.cancel();
    };
  }, [filters, sortConfig]);

  const handleRowClick = (id) => {
    navigate(`/empresas/${id}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Empresas
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/empresas/nueva')}
        >
          Crear Nueva Empresa
        </Button>
      </Box>

      <FilterToolbar
        filters={filters}
        onFilterChange={setFilters}
        statusOptions={[]}
      />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="empresas table">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'nombre'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('nombre')}
                  >
                    Nombre
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'id'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Teléfono</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {empresas.map((empresa) => (
                <TableRow
                  key={empresa.id}
                  hover
                  onClick={() => handleRowClick(empresa.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{highlightText(empresa.nombre, filters.q)}</TableCell>
                  <TableCell>{highlightText(empresa.id.substring(0, 8), filters.q)}</TableCell>
                  <TableCell>{highlightText(empresa.telefono || 'N/A', filters.q)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default EmpresaList;
